import { Droplet } from 'lucide-react';
import { useProgress, useSettings } from '@/lib/store';
import { haptic } from '@/components/ui';
import { ongoingPeriod, periodLength, periodOn } from './fastMetrics';
import { dayLabel } from '@/features/prayer/Prayer';

/* Shown only to users who said they are a sister. Marks the start/end of a
   period (ḥayḍ) or post-natal bleeding (nifās); prayers on those days are
   excused, Ramadan fasts become make-up days. Kept deliberately discreet. */
export function PeriodBanner({ dayKey, today, context }: { dayKey: string; today: string; context: 'prayer' | 'fasting' }) {
  const gender = useSettings((s) => s.settings.gender);
  const periods = useProgress((s) => s.periods);
  const startPeriod = useProgress((s) => s.startPeriod);
  const endPeriod = useProgress((s) => s.endPeriod);
  const deletePeriod = useProgress((s) => s.deletePeriod);
  if (gender !== 'female') return null;

  const on = periodOn(periods, dayKey, today);
  const ongoing = ongoingPeriod(periods);

  if (on) {
    const len = periodLength(on, today);
    const name = on.kind === 'nifas' ? 'Post-natal bleeding (nifās)' : 'Period (ḥayḍ)';
    return (
      <div className="mx-4 mb-4 rounded-2xl bg-[color-mix(in_srgb,#e87ba4_14%,transparent)] p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#e87ba4] text-white"><Droplet size={17} /></span>
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{name} · day {len}</div>
            <p className="text-[13px] leading-snug text-muted">
              {context === 'prayer'
                ? 'Prayers are excused — they are not made up and won’t count against your stats or streaks.'
                : 'Fasting is excused. Ramadan days are added to your make-up (qaḍāʾ) count.'}
            </p>
            {on.kind === 'hayd' && len > 10 && (
              <p className="mt-1 text-[12px] leading-snug text-muted">Bleeding beyond 10 days (Ḥanafī) or 15 days (Shāfiʿī, Mālikī, Ḥanbalī) is usually istiḥāḍah, when prayer is due. Please check with a scholar you trust.</p>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 pl-12">
          {!on.end && (
            <button type="button" onClick={() => { haptic(15); endPeriod(on.id, dayKey); }} className="press rounded-full bg-[#e87ba4] px-3.5 py-1.5 text-[13px] font-semibold text-white">
              Ended {dayLabel(dayKey, today).toLowerCase() === 'today' ? 'today' : `on ${dayLabel(dayKey, today)}`}
            </button>
          )}
          {on.start === dayKey && (
            <button type="button" onClick={() => { haptic(); deletePeriod(on.id); }} className="press rounded-full bg-fill px-3.5 py-1.5 text-[13px] font-semibold text-muted">Undo</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 mb-3 flex justify-end">
      <button type="button" disabled={!!ongoing && ongoing.start <= dayKey} onClick={() => { haptic(15); startPeriod(dayKey); }}
        className="press flex items-center gap-1.5 rounded-full bg-fill px-3 py-1.5 text-[13px] font-semibold text-muted disabled:opacity-40">
        <Droplet size={14} className="text-[#e87ba4]" /> Period started {dayKey === today ? 'today' : 'this day'}
      </button>
    </div>
  );
}
